import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";

export type Tone = "neutral" | "brand" | "info" | "success" | "warning" | "danger" | "promo";

/** Soft (tinted) tone recipes shared by Banner, IconTile, Badge-like elements. */
export const toneSoft: Record<Tone, { surface: string; text: string; icon: string }> = {
  neutral: { surface: "bg-sunken border-border", text: "text-foreground", icon: "text-muted-foreground" },
  brand: { surface: "bg-tone-primary-soft border-tone-primary-border", text: "text-foreground", icon: "text-tone-primary-text" },
  info: { surface: "bg-tone-info-soft border-tone-info-border", text: "text-tone-info-text", icon: "text-tone-info-text" },
  success: { surface: "bg-tone-success-soft border-tone-success-border", text: "text-tone-success-text", icon: "text-tone-success-text" },
  warning: { surface: "bg-tone-warning-soft border-tone-warning-border", text: "text-tone-warning-text", icon: "text-tone-warning-text" },
  danger: { surface: "bg-tone-danger-soft border-tone-danger-border", text: "text-tone-danger-text", icon: "text-tone-danger-text" },
  promo: { surface: "bg-tone-promo-soft border-tone-promo-border", text: "text-tone-promo-text", icon: "text-tone-promo-text" },
};

const toneSolid: Record<Tone, string> = {
  neutral: "bg-foreground text-background",
  brand: "bg-primary text-primary-foreground",
  info: "bg-info text-info-foreground",
  success: "bg-success text-success-foreground",
  warning: "bg-warning text-warning-foreground",
  danger: "bg-destructive text-destructive-foreground",
  promo: "bg-promo text-promo-foreground",
};

export interface IconTileProps {
  icon: LucideIcon;
  tone?: Tone;
  /** `soft` = tinted background, `solid` = filled. */
  variant?: "soft" | "solid";
  size?: "sm" | "md" | "lg";
  className?: string;
}

/** Square icon container used to anchor banners, list rows and empty states. */
export function IconTile({ icon: Icon, tone = "brand", variant = "soft", size = "md", className }: IconTileProps) {
  const box = { sm: "h-7 w-7", md: "h-9 w-9", lg: "h-11 w-11" }[size];
  const glyph = { sm: "h-3.5 w-3.5", md: "h-4 w-4", lg: "h-5 w-5" }[size];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-control",
        variant === "solid" ? toneSolid[tone] : cn(toneSoft[tone].surface, toneSoft[tone].icon, "border"),
        box,
        className,
      )}
      aria-hidden
    >
      <Icon className={glyph} />
    </span>
  );
}

export interface BannerProps {
  tone?: Tone;
  icon?: LucideIcon;
  title?: ReactNode;
  children?: ReactNode;
  /** Right-aligned actions (buttons / links). Stack below text on mobile. */
  actions?: ReactNode;
  /** `strip` = full-bleed edge-to-edge notice; `card` = rounded inset callout. */
  layout?: "strip" | "card";
  className?: string;
}

/**
 * Banner / Callout — the single pattern for page-level notices (KYC, RM pushed cart, promos).
 * Replaces ad-hoc `bg-amber-500/15 border-amber-500/30 …` blocks.
 */
export function Banner({ tone = "neutral", icon, title, children, actions, layout = "card", className }: BannerProps) {
  const t = toneSoft[tone];
  return (
    <div
      role="status"
      className={cn(
        "flex flex-col gap-2.5 sm:flex-row sm:items-center sm:justify-between",
        t.surface,
        layout === "strip" ? "border-b px-3.5 py-2.5" : "rounded-xl border px-3 py-2.5 sm:px-3.5",
        className,
      )}
    >
      <div className="flex min-w-0 items-start gap-2.5 sm:items-center">
        {icon &&
          (layout === "card" ? (
            <IconTile icon={icon} tone={tone} size="sm" />
          ) : (
            (() => {
              const Icon = icon;
              return <Icon className={cn("mt-0.5 h-4 w-4 shrink-0 sm:mt-0", t.icon)} aria-hidden />;
            })()
          ))}
        <div className="min-w-0">
          {title && <div className={cn("type-heading flex flex-wrap items-center gap-1.5 text-foreground")}>{title}</div>}
          {children && (
            <div className={cn("type-caption", title ? "text-muted-foreground" : t.text)}>{children}</div>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}
